import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { faAlignLeft, faBoxes, faChevronRight, faPlus, faSeedling } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, FilterSettingsModel, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { KwFilterDropdownService } from '../../../../../core/widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { FilterSettings } from '../../../../../core/widgets/kw-filter-dropdown/models/filter-options';
import { FilterResults } from '../../../../../core/widgets/kw-filter-dropdown/models/filter-results';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { InventoryItemDTO } from '../../../models/item';
import { InventoryItemEditComponent } from '../inventory-item-edit/inventory-item-edit.component';

const MOBILE_PAGE_SIZE = 25;

@Component({
  selector: 'inventory-items',
  templateUrl: './inventory-items.component.html',
  styleUrls: ['./inventory-items.component.scss']
})
export class InventoryItemsComponent implements OnInit {
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;

  private destroyRef = inject(DestroyRef);

  public faAlignLeft = faAlignLeft;
  public faBoxes = faBoxes;
  public faChevronRight = faChevronRight;
  public faPlus = faPlus;
  public faSeedling = faSeedling;

  public items: InventoryItemDTO[] = [];
  public filteredItems: InventoryItemDTO[] = [];
  public columns: ColumnModel[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;
  public isLoading: boolean = true;
  private initialLoad: boolean = true;
  public filterResults: FilterResults;

  public pageSettings: PageSettingsModel = {
    pageSize: 50,
    pageSizes: [50, 100, 200]
  };

  public filterSettings: FilterSettingsModel = {
    mode: 'Immediate',
    type: 'Excel'
  };

  public dropdownSettings: FilterSettings = {
    roomSettings: {
      display: false,
      singleSelection: false,
    },
    warehouseSettings: {
      display: false,
      singleSelection: false,
    },
    locationSettings: {
      display: false,
      singleSelection: false,
    },
    lotNumberSettings: {
      display: false,
      singleSelection: false,
    },
    inventoryItemSettings: {
      display: true,
      singleSelection: false,
    },
    varietySettings: {
      display: true,
      singleSelection: false,
    },
  };

  constructor(
    private inventoryService: InventoryService,
    private errorService: ErrorService,
    private router: Router,
    private dialog: MatDialog,
    public filterService: KwFilterDropdownService
  ) { }

  ngOnInit() {
    this.columns = [
      { field: 'name', headerText: 'Name' },
      { field: 'description', headerText: 'Description' },
      { field: 'varietyName', headerText: 'Variety' },
      { field: 'packageTypeName', headerText: 'Package Type' },
      { headerText: '', template: this.actionTemplate, width: 80, allowFiltering: false, allowSorting: false },
    ];
    this.updateTable();
  }

  updateTable() {
    this.isLoading = true;
    this.inventoryService.getInventoryItems().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: x => {
        this.items = [...(x ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        this.applyFilters();
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  onRowSelected(args: RowSelectEventArgs) {
    const item = args?.data as InventoryItemDTO;
    if (item) this.openDetail(item);
  }

  openDetail(item: InventoryItemDTO) {
    if (!item?.id) return;
    this.router.navigate(['/harvest-engine/inventory/inventory-item-detail'], { queryParams: { item: item.id } });
  }

  createItem() {
    InventoryItemEditComponent.open(this.dialog, { item: {}, isPopup: true })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(item => {
        if (item)
          this.updateTable();
      });
  }

  setFilterResults(results: FilterResults) {
    if (this.initialLoad) {
      this.initialLoad = false;
      this.filterResults = this.filterService.getFilterState();
    } else {
      this.filterResults = results;
    }
    this.applyFilters();
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByItem(_: number, item: InventoryItemDTO): number | undefined {
    return item.id;
  }

  private applyFilters() {
    const itemIds = (this.filterResults?.selectedInventoryItems ?? []).map(x => x.id);
    const varietyIds = (this.filterResults?.selectedVarieties ?? []).map(x => x.id);
    this.filteredItems = this.items.filter(x =>
      (!itemIds.length || itemIds.includes(x.id)) &&
      (!varietyIds.length || varietyIds.includes(x.varietyId))
    );
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }
}
